import { z } from "zod";
import { dateStringSchema, sqlNumberSchema, txStatusSchema } from "./common.js";
/**
 * The filterable dimensions of a transaction, shared by the server (query
 * schema, SQL column map) and the web filter panel (knob rendering, labels,
 * chip text). Every dimension is a denormalized scalar column on
 * `transactions`, so filtering and aggregating never join.
 *
 * Teardown gas limits and priority fees are deliberately absent: the indexer
 * does not extract them (they only exist inside tx_raw_archive), so they need
 * extractor + backfill work before they can become dimensions here.
 */
/** Bucket count for facet histograms; integer dims with a smaller span get one bucket per value. */
export const TX_FACET_BUCKETS = 40;
const DIMENSIONS = [
    { key: "numNoteHashes", column: "num_note_hashes", label: "Note hashes", group: "Shape", scale: "linear", nullable: false },
    { key: "numNullifiers", column: "num_nullifiers", label: "Nullifiers", group: "Shape", scale: "linear", nullable: false },
    { key: "numL2ToL1Msgs", column: "num_l2_to_l1_msgs", label: "L2→L1 msgs", group: "Shape", scale: "linear", nullable: false },
    { key: "numPrivateLogs", column: "num_private_logs", label: "Private logs", group: "Shape", scale: "linear", nullable: false },
    { key: "numPublicLogs", column: "num_public_logs", label: "Public logs", group: "Shape", scale: "linear", nullable: true },
    { key: "numContractClassLogs", column: "num_contract_class_logs", label: "Class logs", group: "Shape", scale: "linear", nullable: false },
    { key: "numPublicDataWrites", column: "num_public_data_writes", label: "Storage writes", group: "Shape", scale: "linear", nullable: true },
    { key: "gasLimitDa", column: "gas_limit_da", label: "DA mana limit", group: "Mana", scale: "log", nullable: true },
    { key: "gasLimitL2", column: "gas_limit_l2", label: "L2 mana limit", group: "Mana", scale: "log", nullable: true },
    { key: "maxFeePerDaGas", column: "max_fee_per_da_gas", label: "Max fee/DA mana", group: "Fees", scale: "log", nullable: true },
    { key: "maxFeePerL2Gas", column: "max_fee_per_l2_gas", label: "Max fee/L2 mana", group: "Fees", scale: "log", nullable: true },
    { key: "actualFee", column: "actual_fee", label: "Actual fee", group: "Fees", scale: "log", nullable: true, cast: "numeric" },
    { key: "numSetupCalls", column: "num_setup_calls", label: "Setup calls", group: "Calls", scale: "linear", nullable: false },
    { key: "numAppCalls", column: "num_app_calls", label: "App calls", group: "Calls", scale: "linear", nullable: false },
    { key: "totalPublicCalldataSize", column: "total_public_calldata_size", label: "Calldata size", group: "Calls", scale: "log", nullable: false },
];
export const TX_FILTER_DIMENSIONS = DIMENSIONS;
export const TX_FILTER_GROUPS = ["Shape", "Mana", "Fees", "Calls"];
/** Query param pair for a dimension's range filter, e.g. numNullifiers -> [minNumNullifiers, maxNumNullifiers]. */
export function rangeParamNames(key) {
    const cap = (key.charAt(0).toUpperCase() + key.slice(1));
    return [`min${cap}`, `max${cap}`];
}
// ── Facets: network-wide per-dimension distributions (GET /txs/facets) ──
export const txFacetSchema = z.object({
    key: z.string(),
    scale: z.enum(["linear", "log"]),
    /** Rows where the dimension is set; the histogram covers exactly these. */
    nonNullCount: sqlNumberSchema,
    // Display-grade numbers — filtering compares ::numeric server-side, so the
    // Number() precision loss on wei-scale actualFee never affects results.
    min: sqlNumberSchema.nullable(),
    max: sqlNumberSchema.nullable(),
    mean: sqlNumberSchema.nullable(),
    p50: sqlNumberSchema.nullable(),
    p95: sqlNumberSchema.nullable(),
    /** Bucket boundaries, length counts.length + 1; [] when no data. */
    edges: z.array(z.number()),
    counts: z.array(z.number()),
    /**
     * Only when the request named clusters: per cluster, the part of `counts`
     * that belongs to it (same buckets). Clusters are disjoint, so these never
     * sum past `counts` — the rest is everything outside the selection.
     */
    clusterCounts: z.array(z.object({ clusterId: z.number(), counts: z.array(z.number()) })).optional(),
});
export const txFacetsResponseSchema = z.object({
    txCount: sqlNumberSchema,
    facets: z.array(txFacetSchema),
});
/** Days of daily activity the stats endpoint returns. */
export const TX_ACTIVITY_DAYS = 30;
// ── Stats over the filtered set (GET /txs/stats?<same params as the list>) ──
export const txFilterStatsResponseSchema = z.object({
    networkTotal: sqlNumberSchema,
    matching: sqlNumberSchema,
    /** matching / networkTotal * 100; 0 when the network has no txs. */
    pct: z.number(),
    /** Over the mined subset of the matching set (actual_fee is NULL until proposed). Wei-scale strings. */
    fee: z.object({
        count: sqlNumberSchema,
        min: z.string().nullable(),
        max: z.string().nullable(),
        mean: z.string().nullable(),
        median: z.string().nullable(),
    }),
    statuses: z.array(z.object({ status: txStatusSchema, count: sqlNumberSchema })),
    /** When the matching set was active, by first_seen_at (tracks chain time, backfill included). */
    activity: z.object({
        last24h: sqlNumberSchema,
        firstSeen: dateStringSchema.nullable(),
        lastSeen: dateStringSchema.nullable(),
        /** Matching txs per UTC day, TX_ACTIVITY_DAYS entries, oldest first, the last one today. */
        daily: z.array(z.number()),
    }),
});
// ── Insights over the filtered set (GET /txs/insights?<same params as the list>) ──
// Backs the saved-view page: where the matching txs sit in the latest clustering
// run, and what they paid.
/** Upper bound on fee points returned — the most recent mined matches win. */
export const TX_INSIGHTS_FEE_POINTS = 2000;
export const txInsightsClusterSchema = z.object({
    clusterId: z.number(),
    /** Matching txs in this cluster. */
    matching: sqlNumberSchema,
    /** Every tx in this cluster for the run, matching or not — the privacy set size. */
    size: sqlNumberSchema,
});
export const txInsightsFeePointSchema = z.object({
    txHash: z.string(),
    blockNumber: sqlNumberSchema,
    /** Wei-scale integer string. */
    actualFee: z.string(),
    /** Cluster in the insights run (-1 outlier); null when the run never saw the tx. */
    clusterId: z.number().nullable(),
});
export const txInsightsResponseSchema = z.object({
    /** Latest cluster run for the network; null before the first analysis. */
    run: z.object({ id: z.number(), computedAt: dateStringSchema }).nullable(),
    /** Matching txs by where they sit in that run. matching = analyzed + unanalyzed. */
    matching: sqlNumberSchema,
    analyzed: sqlNumberSchema,
    /** Matching txs with no membership in the run (arrived after it, or pending). */
    unanalyzed: sqlNumberSchema,
    /** Every cluster (outliers as clusterId -1) holding at least one matching tx, biggest share first. */
    clusters: z.array(txInsightsClusterSchema),
    /** Most recent mined matches, oldest first; capped at TX_INSIGHTS_FEE_POINTS. */
    feePoints: z.array(txInsightsFeePointSchema),
    /** Mined matches in total — when above feePoints.length the scatter is a recent sample. */
    feePointsTotal: sqlNumberSchema,
});
//# sourceMappingURL=tx-filters.js.map